import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { SITE, WHATSAPP_URL } from "@/constants/site";

const LEGAL_ROWS = [
  ["SIREN", SITE.siren],
  ["SIRET (siège)", SITE.siret],
  ["N° TVA", SITE.tva],
  ["RCS", SITE.rcs],
  ["Forme juridique", "SASU"],
  ["Capital social", SITE.capital],
  ["Code APE", `${SITE.ape} — Terrassement`],
  ["Dirigeant", SITE.leader],
];

export const CompanyInfo = () => (
  <section id="contact" data-testid="company-info" className="py-24 sm:py-32 border-t border-[#21262D]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14"
      >
        <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E5A93C] mb-4">L'entreprise</p>
        <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#F0F6FC] leading-tight max-w-2xl">
          Basés à Maripasoula, au service de toute la Guyane
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5 bg-[#161B22] border border-[#21262D] rounded-2xl p-7 sm:p-9"
          data-testid="company-legal-card"
        >
          <div className="bg-white rounded-xl p-4 mb-6 flex justify-center" data-testid="company-logo-card">
            <img src="/logo-tplm.jpg" alt="Logo TPLM Guyane — Transport, vente de matériaux, location d'engins et de véhicules, terrassement" className="w-full max-w-xs object-contain" />
          </div>
          <h3 className="font-display font-bold uppercase tracking-tight text-xl text-[#F0F6FC] mb-6">
            {SITE.fullName}
          </h3>
          <dl className="flex flex-col divide-y divide-[#21262D]">
            {LEGAL_ROWS.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-4 py-3">
                <dt className="text-xs text-[#8B949E] uppercase tracking-wider">{k}</dt>
                <dd className="font-mono text-xs text-[#F0F6FC] text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </motion.div>

        <div className="lg:col-span-7 flex flex-col gap-4">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-[#161B22] border border-[#21262D] rounded-2xl p-7 sm:p-9"
            data-testid="company-contact-card"
          >
            <p className="flex items-start gap-3 text-sm text-[#8B949E] leading-relaxed mb-7">
              <MapPin size={18} className="text-[#F2541B] shrink-0 mt-0.5" />
              {SITE.address}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="contact-call-btn"
                className="flex items-center gap-2 bg-[#F2541B] hover:bg-[#D9420B] text-white font-display font-bold uppercase text-sm tracking-wide px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                <Phone size={16} /> Appeler
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center gap-2 border border-[#2D5A40] hover:bg-[#1B3B2B]/50 text-[#7BD8A4] font-display font-bold uppercase text-sm tracking-wide px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-btn"
                className="flex items-center gap-2 border border-[#21262D] hover:border-[#8B949E] text-[#F0F6FC] font-display font-bold uppercase text-sm tracking-wide px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                <Mail size={16} /> Email
              </a>
            </div>
            <p className="font-mono text-xs text-[#8B949E] mt-6" data-testid="contact-phone-display">
              {SITE.phoneDisplay} · {SITE.email}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 min-h-[280px] rounded-2xl overflow-hidden border border-[#21262D]"
            data-testid="company-map"
          >
            <iframe
              title="Carte de Maripasoula, Guyane"
              src="https://maps.google.com/maps?q=Maripasoula%20Guyane&t=&z=11&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full min-h-[280px] grayscale invert-[0.9] contrast-[0.9]"
              loading="lazy"
            />
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
